import { FieldState, FormState } from "formstate";
import { partition } from "lodash";
import { observer } from "mobx-react";
import React, { useState, useEffect } from "react";
import { AgentInput } from "@fidget/common/components/form/agentInput";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { salesTaxService } from "@fidget/common/services/salesTaxService";
import { stripeService } from "@fidget/common/services/stripeService";
import { logger } from "@fidget/common/utils";
import { unwrapFormState } from "@fidget/common/utils/unwrapFormState";
// import { validateRequired } from "@fidget/common/utils/validatorsFormState";
import { CountryAutocomplete } from "../../beforeLogin/signup/countryAutocomplete";
import { PaymentSummary } from "../../beforeLogin/signup/paymentSummary";
import { AgentButton } from "@fidget/common/components/inputs/buttons/agentButton/agentButton";
import { ProgressDots } from "../../progressDots/progressDots";
import styles from "./upgradePlan.css";
import { PLAN_DESCRIPTIONS, PlanType } from "./upgradeToPlanAgent";
import { useStore } from "../../../models";
import { staticConfig } from "@fidget/common/config/static-config";
import { useAgentServices } from "../../../models/agentServices";
import { Country, countryService } from "@fidget/common/services/countries";

type PaymentFormFields = {
  country: FieldState<Partial<Country>>;
  companyID: FieldState<string>;
};

interface Props {
  price?: number;
  currency?: string;
}

export const UpgradeToAccelerateAdmin = observer(({ price, currency }: Props) => {
  const totalSteps = 2;
  const [displayMode, agentsCount, paymentFields, submitPayment, moveToPayment, onChange] =
    UpgradeToAccelerateState();

  const renderDescription = () => (
    <>
      <h2 className={styles.title}>
        <L10n id={"upgradePlan.upgradeToGet"} />
      </h2>
      <ProgressDots fulfilledSteps={1} totalSteps={totalSteps} />
      <div className={styles.content}>
        <p>
          <L10n id={"upgradePlan.accelerate.everything"} />
        </p>
        <ul className={styles["accelerate-description"]}>
          {PLAN_DESCRIPTIONS[PlanType.ACCELERATE].map((item) => (
            <li key={item}>
              <L10n id={item} />
            </li>
          ))}
        </ul>
        <div className={styles.pushdown}>
          <AgentButton fullWidth buttonStyle={"big orange"} onClick={moveToPayment}>
            <L10n id={"upgradePlan.accelerate.next"} />
          </AgentButton>
        </div>
      </div>
    </>
  );

  const renderPayment = () => {
    return (
      <>
        <h2 className={styles.title}>
          <L10n id={"upgradePlan.upgrade"} />
        </h2>
        <ProgressDots fulfilledSteps={totalSteps} totalSteps={totalSteps} />
        <div className={styles.content}>
          <p>
            <L10n id={"upgradePlan.accelerate.selectBillingCountry"} />
          </p>
          {/* TODO: update with components (don't know what she meant) */}
          <form id="free-account-payment" onSubmit={submitPayment}>
            <div className={styles["payment-form"]}>
              <CountryAutocomplete field={paymentFields.country} />
              <AgentInput
                className={styles["vat-id"]}
                field={paymentFields.companyID}
                onChange={onChange}
                styleType={"signup"}
                placeholder={intl.formatMessage("signup.payment.placeholder.VAT")}
              />
            </div>
            <div className={styles.pushdown}>
              <PaymentSummary
                country={paymentFields.country.value?.iso}
                companyId={paymentFields.companyID.$}
                agentsNumber={agentsCount}
                planPrice={price || 49}
                currency={currency || "USD"}
              />
              <div className={styles.saveButton}>
                <div className={styles.pushdown}>
                  <AgentButton
                    fullWidth
                    buttonStyle={"big orange"}
                    type="submit"
                    disabled={!paymentFields.country.$}
                    role="link"
                  >
                    <L10n id={"upgradePlan.accelerate.save"} />
                  </AgentButton>
                </div>
              </div>
            </div>
          </form>
          <div className={styles["redirected-to-stripe"]}>
            <L10n id={"upgradePlan.accelerate.rediredtedToStripe"} />
          </div>
        </div>
      </>
    );
  };

  switch (displayMode) {
    case "payment":
      return renderPayment();
    default:
      return renderDescription();
  }
});

const UpgradeToAccelerateState = (): [
  "default" | "payment",
  number,
  PaymentFormFields,
  (ev: React.FormEvent) => void,
  () => void,
  (ev: React.ChangeEvent<HTMLInputElement>) => void
] => {
  const { spinnerService } = useAgentServices();
  const { currentUserStore } = useStore();
  const [displayMode, setDisplayMode] = useState<"default" | "payment">("default");
  const [agentsCount, setAgentsCount] = useState<number>(1);
  const [ipAddress, setIpAddress] = useState<string>("");
  const [countryIso, setCountryIso] = useState<string>("");
  const [vatId, setVatId] = useState<string>("");
  const [paymentFields, setPaymentFields] = useState<PaymentFormFields>({
    country: new FieldState(countryService.countryByIso(countryIso) || {}).validators((val?: Partial<Country>) =>
      validateCountry(val)
    ),
    companyID: new FieldState(vatId).validators((val?: string) =>
      validateIDCompany(val, countryIso)
    )
  });
  const [paymentForm, setPaymentForm] = useState<FormState<PaymentFormFields>>();

  useEffect(() => {
    loadData();

    const fetchGeo = async () => {
      try {
        const { country, ip } = await (
          await fetch("https://get.geojs.io/v1/ip/country.json")
        ).json();
        setCountryIso(country);
        setIpAddress(ip);
      } catch (e) {
        logger.error({ message: "Geo fetch error", error: e });
      }
    };
    fetchGeo();
  }, []);

  useEffect(() => {
    setPaymentFields({
      country: new FieldState(countryService.countryByIso(countryIso) || {}).validators((val?: Partial<Country>) =>
        validateCountry(val)
      ),
      companyID: new FieldState(vatId).validators((val?: string) =>
        validateIDCompany(val, countryIso)
      )
    });
  }, [vatId, countryIso]);

  useEffect(() => {
    // it might be a little hacky to update those states, but this lib don't want to cooperate
    setPaymentForm(new FormState(paymentFields));
  }, [paymentFields]);

  const moveToPayment = () => {
    setDisplayMode("payment");
  };

  const loadData = async () => {
    Promise.all([spinnerService.getAgentProfiles(), spinnerService.getPendingAgents()]).then(
      ([agents, pendingAgents]) => {
        const allAgents = partition(agents, "isActive");
        const activeAgents = allAgents[0].filter((adviser) => !adviser.isBot);
        setAgentsCount(activeAgents.length + pendingAgents.length);
      }
    );
  };

  const onChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    ev.preventDefault();
    setVatId(ev.target.value);
  };

  const submitPayment = async (ev: React.FormEvent) => {
    ev.preventDefault();

    const resultPaymentForm = await paymentForm!.validate();
    if (!resultPaymentForm.hasError) {
      const formResult = {
        companyDetails: {
          ...(unwrapFormState(paymentForm!.$) as any)
        }
      };

      const upgradeForm = {
        planName: staticConfig.newestAccelerate,
        vatId: formResult.companyDetails.companyID,
        country: formResult.companyDetails.country,
        ipAddress
      };

      try {
        const { sessionId } = await currentUserStore.upgradeOrg(upgradeForm);
        await stripeService.load();
        await stripeService.redirectToCheckoutWithSessionID(sessionId);
      } catch (e) {
        logger.error({ message: "Upgrade org error", error: e });
      }
    }
  };

  return [displayMode, agentsCount, paymentFields, submitPayment, moveToPayment, onChange];
};

const validateIDCompany = async (val?: string, countryIso?: string) => {
  if (countryIso && val) {
    const isIDvalid = await salesTaxService.validateTaxNumber(countryIso, val);
    return isIDvalid ? "" : intl.formatMessage("signup.payment.placeholder.VAT.error");
  }
  return "";
};

const validateCountry = async (val?: Partial<Country>) =>
  val?.iso ? "" : "No such country";
